import { Col, Form, message, Modal, Row, Spin, Tabs } from 'antd';
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import config from '../../../config';
import { getAsset } from '../../../services/general';
import {
  consultationService,
  expirationDate,
  saveReports,
} from "../../../services/medicalConsultation";
import { handlers } from './../../../helpers/helpers';
import { base64ToStream, uploadAssetController } from './../../../utils/fileUtils';
import ExamTabs from "./ExamTabs";
import Health from "./formTypes/Health";
import ConsultationDiagnostics from "./ConsultationDiagnostics";
import PsychologicalOne from "./formTypes/PsychologicalOne";
import PsychologicalTwo from "./formTypes/PsychologicalTwo";
import { getCookie } from '../../../services/cookies';
import Notes from '../../components/Notes';
import { willTriggerDeferredExamNotification } from './utils/deferredExamUtils';

const { TabPane } = Tabs;
const errorList = require('../../../utils/errorsList.json');

const ConsultationEdit = ({
  id,
  disableForm,
  loading,
  examsList,
  serviceIndex,
  currentFormType,
  patientFullName,
  fetchMedicalConsultationDetails,
  setIsEditingMode,
  medicalConsultationDetails,
  serviceDetail,
  patientData,
  unfinishedExams,
  approvedTextList,
  countryCode,
  setConsultationStatusType,
  validatedExams,
  setStatus,
  isPermittedRole,
  urlParams,
  reevaluationState,
  refreshMedicalConsultationDetails,
  countrySettings,
  isUnableToChoose,
  medicalConsultationValidation,
  allowPendingConsultationSign,
  reprovedResultsMap = new Map(),
  examListInSidebar = false,
  activeExamTab,
  setActiveExamTab,
  setExamTabItems,
  examContentRef,
  activeConsultationTab,
  setActiveConsultationTab,
  generatedPrescriptions,
  hasDeferredExamPrescription,
  deferredExamPrescriptionState,
}) => {
  const [loadingConsultation, setLoadingConsultation] = useState({
    save: false,
    saveAndFinalize: false,
    sign: false,
    downloadDoc: false,
    presign: false,
  });
  const lastSavedParamsRef = useRef(null);
  const [reevaluationDocuments, setReevaluationDocuments] = useState();
  const [unfinishedExamsFiltered, setUnfinishedExamsFiltered] = useState(
    unfinishedExams && unfinishedExams.length > 0 && medicalConsultationDetails
      ? unfinishedExams.filter((exam) =>
        exam.service_id === medicalConsultationDetails.service_id
        && exam.service_type === medicalConsultationDetails.service_type
      )
      : unfinishedExams
  );
  const [showConfirmReprovingValues, setShowConfirmReprovingValues] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslation();

  const roles = getCookie('roles');
  const oldReport = config.oldReportCompanyIds?.includes(patientData?.company_id);
  const oldReportCountry = countrySettings.medicalConsultation.newReport[countryCode];
  const showMedicalEvaluationTabs = medicalConsultationDetails?.id &&
    countrySettings.medicalConsultation.medicalEvaluationTabs[countryCode];
  const showDiagnosticsTab = medicalConsultationDetails?.id &&
    countrySettings.medicalConsultation.showDiagnosticsSection[countryCode] && ['medical_auditor', 'doctor'].some(role => roles.includes(role));
  const showNotes = medicalConsultationDetails?.id && countrySettings.medicalConsultation.showNotes[countryCode]
    && roles.some((role) => ['doctor', 'medical_auditor', 'direccion_medica'].includes(role));

  useEffect(() => {
    if (medicalConsultationDetails) {
      setUnfinishedExamsFiltered(
        unfinishedExams?.length > 0 && medicalConsultationDetails
          ? unfinishedExams.filter((exam) =>
            exam.service_id === medicalConsultationDetails.service_id
            && exam.service_type === medicalConsultationDetails.service_type
          )
          : unfinishedExams
      );
    }
  }, [medicalConsultationDetails, unfinishedExams]);

  const fetchReevaluationDocuments = useCallback(async () => {
    if (medicalConsultationDetails && medicalConsultationDetails.id) {
      try {
        let response = await consultationService.reevaluationDocuments({ id: medicalConsultationDetails.id });
        setReevaluationDocuments(response);
      } catch (error) {
        console.log(error);
      }
    }
  }, [medicalConsultationDetails]);

  useEffect(() => {
    fetchReevaluationDocuments();
  }, [fetchReevaluationDocuments])

  const reevaluationDocs = (documents) => {
    return (
      <Row>
        <p className="bold">Documentos de reevaluación</p>
        <Col lg={24} xs={24} className='reevaluation-docs-wrapper pt-20 pl-20 pr-20 pb-10'>
          {
            loading.downloadDoc ?
              <Row justify='center'>
                <Spin className='cmt-spin mt-40' />
              </Row>
              :
              documents.filter(documentItem => documentItem.exam_document_id || documentItem.note_document_id).map((documentItem, index) => {
                return (
                  <Row key={index}>
                    <Col lg={24} xs={24}>
                      <p
                        onClick={() => downloadDocument(documentItem)}
                        className='document-text mb-10'
                      >
                        {documentItem.name}
                      </p>
                    </Col>
                  </Row>
                );
              })
          }
        </Col>
      </Row>
    );
  };

  const downloadDocument = async (documentItem) => {
    try {
      setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, downloadDoc: true } });
      const params = documentItem.is_note_document ? {
        id: documentItem.note_document_id,
        type: "NotesDocuments",
        field: "document"
      } : {
        id: documentItem.exam_document_id,
        type: "ExamsDocument",
        field: "document"
      };
      const response = await getAsset(params);

      if (response.document_url) {
        await handlers.urlDownload(response.document_url, message);
      } else {
        message.error({
          content: `${t('medicalConsultations.consultationEdit.downloadDocError')}`,
          style: {
            marginTop: '40vh',
          },
        });
      }

    } catch (error) {
      console.error('Error interno', error);
      message.error({
        content: `Error interno: ${error}. Inténtalo nuevamente`,
        style: {
          marginTop: '40vh',
        },
      });
    } finally {
      setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, downloadDoc: false } });
    }
  };

  const generateReport = async (id, role, saveReport = false, withSignature = true) => {
    return consultationService.createMedicalConsultationReport({
      id,
      department: (currentFormType === 'health' || currentFormType === 'normal') ? 'health' : 'psychological',
      role,
      saveReport,
      ...(!withSignature && { signer_admin_id: -1 })
    });
  }

  const saveReportsHandler = async (id, role) => {
    let response;
    try {
      response = await generateReport(id, role);
      if (response?.pdf) {
        const [reports] = await saveReports({
          medical_consultation_detail_id: id,
          type: 'medical',
          role,
        })
        const patientDocumentNumber = patientData?.document_number;
        const patientFullName = patientData?.full_name;
        const name = {
          admin: `${patientFullName}_${patientDocumentNumber}_Informe-Medico-Resumido`,
          RRHH: `${patientFullName}_${patientDocumentNumber}_RRHH`,
        };
        await uploadAssetController({
          file: base64ToStream(`data:application/pdf;base64,${response.pdf}`, 'application/pdf', `${name[role]}.pdf`),
          object_field: 'document',
          object_id: reports,
          object_type: 'MedicalReport',
        });
      }
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      return response;
    }
  };

  const signMedicalConsultation = async (id, department, deferredEmail) => {
    if (countrySettings.medicalConsultation.setExpirationDate[countryCode] && department === 'psychological') {
      await expirationDate({ id, department_id: 2 });
    }
    const selectedStatusId = form.getFieldValue("status") || examsList[serviceIndex]?.status;
    const selectedStatus = approvedTextList?.find((option) => option.id.toString() === selectedStatusId?.toString());
    const isApprovedStatus = (countrySettings.medicalConsultation.medicalConsultationTracker[countryCode] && selectedStatus?.status === 'approved')
      || (!countrySettings.medicalConsultation.medicalConsultationTracker[countryCode] && selectedStatus?.text === 'Apto');

    let response;
    let responseNotify;
    let textMessage;
    try {
      response = await generateReport(id, '', !countrySettings.medicalConsultation.uploadReport[countryCode]);
      if (response && ((countrySettings.medicalConsultation.uploadReport[countryCode] && response?.pdf) || !countrySettings.medicalConsultation.uploadReport[countryCode])) {
        const pdf = response.pdf;
        response = await consultationService.signServices({
          id,
          ...(countrySettings.medicalConsultation.uploadReport[countryCode] && { pdf }),
          country_code: countryCode,
          department: (currentFormType === 'health' || currentFormType === 'normal') ? 'health' : 'psychological',
          company_id: examsList[serviceIndex]?.company_id,
          override: isUnableToChoose() && isApprovedStatus,
          status: examsList[serviceIndex]?.status,
        });
        if (medicalConsultationDetails.revaluation || deferredEmail) {
          responseNotify = await consultationService.notifyReevaluation({ medicalConsultationDetailId: id, confirmation: true });
        }
        textMessage = `${t('medicalConsultations.consultationEdit.successSigned')}`;
      } else {
        message.error({
          content: "Ha ocurrido un error al firmar la consulta. Inténtalo de nuevo.",
          style: {
            marginTop: "40vh",
          },
        });
      }
    } catch (error) {
      console.error('Error generating report: ', error);
      message.error({
        content: error || "Ha ocurrido un error al firmar la consulta. Inténtalo de nuevo.",
        style: {
          marginTop: "40vh",
        },
      });
    } finally {
      return {
        response,
        responseNotify,
        textMessage,
      };
    }
  }

  const buildSaveParams = (values) => {
    const approved_text = approvedTextList?.find((option) => option.id.toString() === String(values["status"]));

    if (currentFormType === 'health' || currentFormType === 'normal') {
      return {
        id: values["id"] ? values["id"] : null,
        service_id: values["serviceId"],
        service_type: values["serviceType"],
        detail: values["detail"],
        conclusion: values["conclusion"],
        status: values["status"],
        pending_reasons: values["pendingReasons"] ? values["pendingReasons"] : null,
        show_detail: values["showDetail"] ? values["showDetail"] : false,
        show_conclusion: values["showConclusion"] ? values["showConclusion"] : false,
        done: values["saveAndFinalize"] || values["sign"] ? true : false,
        diagnostics: [],
        comment: values["comment"],
        admission_patient_id: values["admissionPatientId"],
        secondary_conclusion: values["secondary_conclusion"],
        medical_form_type: currentFormType,
        approved_text: approved_text?.status,
        indications: values["indication"] && values["indication"] !== "" ? values["indication"] : null,
        suggestions: values["suggestion"] && values["suggestion"] !== "" ? values["suggestion"] : null,
        is_medical_auditor: roles.includes("medical_auditor"),
        ...(countrySettings.medicalConsultation.healthCustomExpirationDate[countryCode]
          && values.expirationDate && { report_expiration_date: moment(values.expirationDate).format('YYYY-MM-DD') }),
        ...(!medicalConsultationDetails?.started_at && { started: true }),
      };
    }

    if (currentFormType === 'psychological_one') {
      return {
        id: values["id"] ? values["id"] : null,
        service_id: values["serviceId"],
        service_type: values["serviceType"],
        admission_patient_id: values["admissionPatientId"],
        medical_form_type: currentFormType,
        driver_license: values["driverLicense"] ? values["driverLicense"].join(';') : values["driverLicense"],
        valid_driver_license: values["validDriverLicense"] && moment(values["validDriverLicense"]).format("YYYY-MM-DD"),
        use_of_glasses: values["useOfGlasses"],
        date_evaluation: values["dateEvaluation"] && moment(values["dateEvaluation"]).format("YYYY-MM-DD"),
        validity_evaluation: values["validityEvaluation"] && moment(values["validityEvaluation"]).format("YYYY-MM-DD"),
        test_data: values["testData"],
        done: values["saveAndFinalize"] || values["sign"] ? true : false,
        applied_evaluation: values["appliedEvaluation"],
        approved_text: approved_text?.status,
        conclusion: values["conclusion"],
        status: values["status"],
        indications: values["indication"] && values["indication"] !== "" ? values["indication"] : null,
        suggestions: values["suggestion"] && values["suggestion"] !== "" ? values["suggestion"] : null,
        is_medical_auditor: roles.includes("medical_auditor"),
        pending_reasons: values["pendingReasons"] ? values["pendingReasons"] : null,
        ...(!medicalConsultationDetails?.started_at && { started: true }),
      };
    }

    if (currentFormType === 'psychological_two') {
      return {
        id: values["id"] ? values["id"] : null,
        service_id: values["serviceId"],
        service_type: values["serviceType"],
        admission_patient_id: values["admissionPatientId"],
        medical_form_type: currentFormType,
        educational_lvl: values["educationalLvl"],
        date_evaluation: values["dateEvaluation"] && moment(values["dateEvaluation"]).format("YYYY-MM-DD"),
        validity_evaluation: values["validityEvaluation"] && moment(values["validityEvaluation"]).format("YYYY-MM-DD"),
        applied_evaluation: values["appliedEvaluation"],
        approved_text: approved_text?.status,
        done: values["saveAndFinalize"] || values["sign"] ? true : false,
        conclusion: values["conclusion"],
        status: values["status"],
        indications: values["indication"] && values["indication"] !== "" ? values["indication"] : null,
        suggestions: values["suggestion"] && values["suggestion"] !== "" ? values["suggestion"] : null,
        is_medical_auditor: roles.includes("medical_auditor"),
        pending_reasons: values["pendingReasons"] ? values["pendingReasons"] : null,
        ...(!medicalConsultationDetails?.started_at && { started: true }),
      };
    }

    return undefined;
  };

  const getComparableParams = (params) => {
    if (!params) return params;
    const { done, started, is_medical_auditor, ...comparableParams } = params;
    return comparableParams;
  };

  const handleFormPopulated = (formValues) => {
    lastSavedParamsRef.current = buildSaveParams(formValues);
  };

  const onFinishConsultation = async (values) => {
    const resetActions = () => {
      form.setFieldsValue({ save: undefined });
      form.setFieldsValue({ saveAndFinalize: undefined });
      form.setFieldsValue({ sign: undefined });
    };

    try {
      setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, form: true } });
      setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, saveAndFinalize: values['saveAndFinalize'] ? true : false } });
      setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, save: !values['saveAndFinalize'] && !values['sign'] ? true : false } });
      setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, sign: values['sign'] ? true : false } });

      let params;
      let response;
      let responseNotify;
      let textMessage;

      if (values?.sign
        && ((unfinishedExamsFiltered
          && Array.isArray(unfinishedExamsFiltered)
          && unfinishedExamsFiltered.length === 0) || countrySettings.medicalConsultation.allowSignWithPending[countryCode])) {

        const willTriggerDeferredEmail = willTriggerDeferredExamNotification({ deferredExamPrescriptionState });
        let confirmed = false;
        if (willTriggerDeferredEmail) {
          confirmed = await new Promise(resolve => {
            Modal.confirm({
              title: 'Confirmación de envío',
              content: 'Firmar esta consulta enviará automáticamente el correo de reevaluación al paciente. ¿Desea continuar?',
              okText: 'Sí, firmar',
              cancelText: 'Cancelar',
              centered: true,
              icon: null,
              className: 'deferred-exam-confirm',
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (!confirmed) {
            resetActions();
            setLoadingConsultation(prev => ({ ...prev, form: false, sign: false }));
            return;
          }
        }

        params = buildSaveParams(values);
        const hasUnsavedChanges = !lastSavedParamsRef.current
          || JSON.stringify(getComparableParams(params)) !== JSON.stringify(getComparableParams(lastSavedParamsRef.current));

        if (hasUnsavedChanges) {
          const saveResponse = await consultationService.saveConsultation(params);
          if (!(saveResponse?.id || saveResponse?.success === true)) {
            message.error({
              content: "No se pudo guardar el estado actual de la consulta antes de firmar. Inténtalo de nuevo.",
              style: {
                marginTop: "40vh",
              },
            });
            resetActions();
            setLoadingConsultation(prev => ({ ...prev, form: false, sign: false }));
            return;
          }
        }

        const id = values["id"];
        const department = (currentFormType === 'health' || currentFormType === 'normal') ? 'health' : 'psychological';
        const signPromises = [signMedicalConsultation(id, department, confirmed)];

        if (countryCode === 'PE' && department === 'health') {
          signPromises.push(saveReportsHandler(id, 'RRHH'));
          signPromises.push(saveReportsHandler(id, 'admin'));
        }

        const results = await Promise.all(signPromises);
        const signResponse = results[0];

        response = signResponse?.response;
        responseNotify = signResponse?.responseNotify;
        textMessage = signResponse?.textMessage;
      } else {
        params = buildSaveParams(values);
        response = await consultationService.saveConsultation(params);
        textMessage = `${t('medicalConsultations.consultationEdit.messageConsultation')} ${values["save"] ? "Guardada" : "Guardada y finalizada"} exitosamente`;
      }

      if (response?.id || response?.success === true) {
        lastSavedParamsRef.current = params;

        message.success({
          content: textMessage,
          style: {
            marginTop: "40vh",
          },
        });

        if (response?.deferredExamNotificationFailed) {
          message.warning({
            content: 'La consulta fue firmada exitosamente, pero hubo un problema al enviar la notificación de reevaluación. Puede reintentarlo firmando nuevamente o guardando la reevaluación.',
            duration: 10,
            style: {
              marginTop: "35vh",
            },
          });
        }

        if (medicalConsultationDetails?.revaluation && responseNotify?.patientResponse?.responseEmail === 400) {
          message.error({
            content: `${t('medicalConsultations.consultationEdit.noContactPatient')}`,
            style: {
              marginTop: "35vh",
            },
          });
        }
        setIsEditingMode(false);
        if (values["id"]) {
          fetchMedicalConsultationDetails();
        } else {
          navigate({
            pathname: `/consulta-medica/${response['id']}`,
            state: {
              from: location.state ? location.state.from : '/consulta-medica/buscar',
              search: location.state && location.state.search ? location.state.search : null,
            },
          });
        }
      } else if (!values?.sign) {
        console.error('Error on finish consultation: ', response);
        message.error({
          content: "Ha ocurrido un error. Inténtalo de nuevo.",
          style: {
            marginTop: "40vh",
          },
        });
      }
    } catch (err) {
      console.error(err)
      const errorMessage = handlers.isJSON(err) ? errorList[JSON.parse(err).code] : err;
      message.error({
        content: `Error interno: ${errorMessage}`,
        style: {
          marginTop: "40vh",
        },
      });
    } finally {
      resetActions();
      setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, form: false } });
      setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, saveAndFinalize: false } });
      setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, save: false } });
      setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, sign: false } });
    }
  };

  const presignConsultation = async () => {
    setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, presign: true } });
    try {
      await consultationService.presignServices({
        id: medicalConsultationDetails.id,
        validate_presign_exams: false,
        company_id: patientData.company_id,
        department: (currentFormType === 'health' || currentFormType === 'normal') ? 'health' : 'psychological',
      })
      message.success({
        content: t('medicalConsultations.consultationEdit.presignSuccess'),
        style: {
          marginTop: "40vh",
        },
      })
      fetchMedicalConsultationDetails();
    } catch (error) {
      console.error(error);
      message.error({
        content: t('medicalConsultations.consultationEdit.presignError'),
        style: {
          marginTop: "40vh",
        },
      });
    } finally {
      setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, presign: false } });
    }
  }

  const cancelPresign = async () => {
    setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, cancelPresign: true } });
    try {
      await consultationService.cancelPresignServices({
        id: medicalConsultationDetails.id,
        company_id: patientData.company_id,
        department: (currentFormType === 'health' || currentFormType === 'normal') ? 'health' : 'psychological',
      })
      message.success({
        content: t('medicalConsultations.consultationEdit.presignCancel'),
        style: {
          marginTop: "40vh",
        },
      })
      fetchMedicalConsultationDetails();
    } catch (error) {
      console.error(error);
      message.error({
        content: t('medicalConsultations.consultationEdit.presignCancelError'),
        style: {
          marginTop: "40vh",
        },
      })
    } finally {
      setLoadingConsultation(loadingConsultation => { return { ...loadingConsultation, cancelPresign: false } });
    }
  }

  const hasToShowPopOver = () => (
    validatedExams?.some(exam =>
      (exam.procedures?.some(procedure => (
        /reproves|altered/.test(procedure?.result_type)
        || !procedure.result_type
      )))
      ||
      (exam.measurements?.some(measurement => (
        /reproves|altered/.test(measurement?.result_type)
        || !measurement.result_type
      )))
    )
  );

  const listReproved = () => {
    const combined = {
      reprovedProcedures: [],
      alteredProcedures: [],
    };

    validatedExams?.forEach(exam => {
      const reprovedProcedures = exam?.procedures?.map(procedure => (procedure.result_type?.includes('reproves') || procedure.reprovingItems?.includes(procedure?.result?.toLowerCase())) && procedure.name);
      const reprovedMeasurements = exam?.measurements?.map(measurement => (measurement?.result_type?.includes('reproves') || measurement.reprovingItems?.includes(measurement?.result?.toLowerCase())) && measurement.name);

      const alteredProcedures = exam?.procedures?.map(procedure => {
        const alteredProcedures = procedure.result_type?.toLowerCase().includes('altered');
        const hasAltered = (countrySettings.StatusDetail[countryCode] && procedure.criterion) || !countrySettings.StatusDetail[countryCode]
        return (alteredProcedures && hasAltered) ? procedure.name : '';
      });
      const alteredMeasurements = exam?.measurements?.map(measurement => {
        const alteredMeasurements = measurement.result_type?.toLowerCase().includes('altered');
        const hasAltered = (countrySettings.StatusDetail[countryCode] && measurement.criterion) || !countrySettings.StatusDetail[countryCode]
        return (alteredMeasurements && hasAltered) ? measurement.name : '';
      });

      combined.reprovedProcedures.push(...reprovedProcedures.filter(Boolean), ...reprovedMeasurements.filter(Boolean));
      combined.alteredProcedures.push(...alteredProcedures.filter(Boolean), ...alteredMeasurements.filter(Boolean));
    });

    const status = isUnableToChoose() ? t('medicalConsultations.consultationEdit.statusDisclaimer', {
      parameters: `${combined.reprovedProcedures.length > 0 ? t('medicalConsultations.consultationEdit.reprovedProcedures', { procedures: combined.reprovedProcedures.join(', ') }) : ''}`
    }) : t('medicalConsultations.consultationEdit.statusAlteredDisclaimer', {
      parameters: `${combined.alteredProcedures.length > 0 ? t('medicalConsultations.consultationEdit.alteredApprovedProcedures', { procedures: combined.alteredProcedures.join(', ') }) : ''}`
    });

    return (
      <p>
        {status}
      </p>
    );
  }

  const serviceParams = {
    service_id: medicalConsultationDetails?.service_id || urlParams?.serviceId,
    service_type: medicalConsultationDetails?.service_type || urlParams?.serviceType,
  }

  return (
    <Col xxl={19} xl={18} lg={18} xs={24} className="medical-consultation-detail__edition pt-50 pb-80">
      <Row justify="center">
        <Col lg={20} xs={24}>
          {
            (medicalConsultationDetails && medicalConsultationDetails.revaluation) &&
            <Row className='mb-40'>
              <Col lg={24} xs={24}>
                <h2 className="reevaluation-title">{t('medicalConsultations.consultationEdit.reevaluationTitle')}</h2>
              </Col>
              {
                medicalConsultationDetails && medicalConsultationDetails.revaluation && reevaluationDocuments && reevaluationDocuments.length > 0 &&
                <Col lg={20} xs={24}>
                  {reevaluationDocs(reevaluationDocuments)}
                </Col>
              }
            </Row>
          }
        </Col>
        <Col lg={20} xs={24}>
          <h2 className="mb-20">
            {
              (countrySettings.medicalConsultation.usePrestationTitle?.[countryCode] && (examsList && examsList.length > 0 && examsList[serviceIndex])?.title)
                ? (examsList && examsList.length > 0 && examsList[serviceIndex])?.title
                : (examsList && examsList.length > 0 && examsList[serviceIndex])?.name
            }
            {
              medicalConsultationDetails && medicalConsultationDetails.revaluation &&
              (<span> ({t('medicalConsultations.consultationEdit.reevaluationTitle')})</span>)
            }
          </h2>
        </Col>

        <Col lg={20} xs={22}>
          {showMedicalEvaluationTabs && (showDiagnosticsTab || showNotes) && (currentFormType === 'health' || currentFormType === 'normal') ? (
            <Tabs activeKey={activeConsultationTab} onChange={setActiveConsultationTab} destroyInactiveTabPane={false} className='cmt-tabs'>
              <TabPane tab={<strong>{t('medicalConsultations.consultationEdit.medicalEvaluationTab')}</strong>} key="health" forceRender={true}>
                {currentFormType && (currentFormType === 'health' || currentFormType === 'normal') && (
                  <Health
                    id={id}
                    form={form}
                    disableForm={disableForm}
                    servicesList={examsList}
                    serviceIndex={serviceIndex}
                    onFinishConsultation={onFinishConsultation}
                    onPresignConsultation={presignConsultation}
                    cancelPresign={cancelPresign}
                    generateReport={generateReport}
                    setIsEditingMode={setIsEditingMode}
                    onFormPopulated={handleFormPopulated}
                    loadingConsultation={loadingConsultation}
                    medicalConsultationDetails={medicalConsultationDetails}
                    patientData={patientData}
                    patientFullName={patientFullName}
                    unfinishedExams={unfinishedExamsFiltered}
                    approvedTextList={approvedTextList}
                    loading={loading}
                    countryCode={countryCode}
                    setConsultationStatusType={setConsultationStatusType}
                    listReproved={listReproved}
                    isUnableToChoose={isUnableToChoose}
                    validatedExams={validatedExams}
                    hasToShowPopOver={hasToShowPopOver}
                    setStatus={setStatus}
                    isPermittedRole={isPermittedRole}
                    reevaluationState={reevaluationState}
                    countrySettings={countrySettings}
                    oldReport={oldReport}
                    oldReportCountry={oldReportCountry}
                    serviceDetail={serviceDetail}
                    showConfirmReprovingValues={showConfirmReprovingValues}
                    setShowConfirmReprovingValues={setShowConfirmReprovingValues}
                    allowPendingConsultationSign={allowPendingConsultationSign}
                    expirationDate={expirationDate}
                    generatedPrescriptions={generatedPrescriptions}
                    hasDeferredExamPrescription={hasDeferredExamPrescription}
                  />
                )}
              </TabPane>
              {showDiagnosticsTab && (
                <TabPane tab={<strong>{t('medicalConsultations.consultationEdit.diagnosticsTab')}</strong>} key="diagnostics" forceRender={true}>
                  <ConsultationDiagnostics
                    medicalConsultationId={medicalConsultationDetails.id}
                    admissionPatientId={id}
                    serviceId={medicalConsultationDetails.service_id}
                    serviceType={medicalConsultationDetails.service_type}
                    medicalFormType={medicalConsultationDetails.medical_form_type || currentFormType}
                    t={t}
                    countrySettings={countrySettings}
                    countryCode={countryCode}
                    patientFullName={patientFullName}
                    oldReport={oldReport}
                    oldReportCountry={oldReportCountry}
                    examsList={serviceDetail}
                    patientData={patientData}
                  />
                </TabPane>
              )}
              {showNotes &&
                <TabPane tab={<strong>{t('medicalConsultations.consultationEdit.notesTitle')}</strong>} key='notes'>
                  <Notes
                    key={medicalConsultationDetails.id}
                    id={medicalConsultationDetails.id}
                    type="medicalConsultation"
                    edit={true}
                    showTagging={true}
                    allowedTags={['interconsultation']}
                    onDocumentsChange={fetchReevaluationDocuments}
                  />
                </TabPane>
              }
            </Tabs>
          ) : (
            <>
              {currentFormType && (currentFormType === 'health' || currentFormType === 'normal') && (
                <Health
                  id={id}
                  form={form}
                  disableForm={disableForm}
                  servicesList={examsList}
                  serviceIndex={serviceIndex}
                  onFinishConsultation={onFinishConsultation}
                  onPresignConsultation={presignConsultation}
                  cancelPresign={cancelPresign}
                  generateReport={generateReport}
                  setIsEditingMode={setIsEditingMode}
                  onFormPopulated={handleFormPopulated}
                  loadingConsultation={loadingConsultation}
                  medicalConsultationDetails={medicalConsultationDetails}
                  patientData={patientData}
                  patientFullName={patientFullName}
                  unfinishedExams={unfinishedExamsFiltered}
                  approvedTextList={approvedTextList}
                  loading={loading}
                  countryCode={countryCode}
                  setConsultationStatusType={setConsultationStatusType}
                  listReproved={listReproved}
                  isUnableToChoose={isUnableToChoose}
                  validatedExams={validatedExams}
                  hasToShowPopOver={hasToShowPopOver}
                  setStatus={setStatus}
                  isPermittedRole={isPermittedRole}
                  reevaluationState={reevaluationState}
                  countrySettings={countrySettings}
                  oldReport={oldReport}
                  oldReportCountry={oldReportCountry}
                  serviceDetail={serviceDetail}
                  showConfirmReprovingValues={showConfirmReprovingValues}
                  setShowConfirmReprovingValues={setShowConfirmReprovingValues}
                  allowPendingConsultationSign={allowPendingConsultationSign}
                  expirationDate={expirationDate}
                  generatedPrescriptions={generatedPrescriptions}
                  hasDeferredExamPrescription={hasDeferredExamPrescription}
                />
              )}
            </>
          )}
          {
            currentFormType && currentFormType === "psychological_one" &&
            <PsychologicalOne
              id={id}
              form={form}
              disableForm={disableForm}
              servicesList={examsList}
              serviceIndex={serviceIndex}
              onFinishConsultation={onFinishConsultation}
              generateReport={generateReport}
              setIsEditingMode={setIsEditingMode}
              onFormPopulated={handleFormPopulated}
              loadingConsultation={loadingConsultation}
              medicalConsultationDetails={medicalConsultationDetails}
              unfinishedExams={unfinishedExamsFiltered}
              approvedTextList={approvedTextList}
              loading={loading}
              countryCode={countryCode}
              patientData={patientData}
              patientFullName={patientFullName}
              listReproved={listReproved}
              isUnableToChoose={isUnableToChoose}
              hasToShowPopOver={hasToShowPopOver}
              setStatus={setStatus}
              countrySettings={countrySettings}
              oldReport={oldReport}
              oldReportCountry={oldReportCountry}
              validatedExams={validatedExams}
              showConfirmReprovingValues={showConfirmReprovingValues}
              setShowConfirmReprovingValues={setShowConfirmReprovingValues}
              generatedPrescriptions={generatedPrescriptions}
              hasDeferredExamPrescription={hasDeferredExamPrescription}
            />
          }

          {
            currentFormType && currentFormType === "psychological_two" &&
            <PsychologicalTwo
              id={id}
              form={form}
              disableForm={disableForm}
              servicesList={examsList}
              serviceIndex={serviceIndex}
              onFinishConsultation={onFinishConsultation}
              generateReport={generateReport}
              setIsEditingMode={setIsEditingMode}
              onFormPopulated={handleFormPopulated}
              loadingConsultation={loadingConsultation}
              medicalConsultationDetails={medicalConsultationDetails}
              patientData={patientData}
              unfinishedExams={unfinishedExamsFiltered}
              approvedTextList={approvedTextList}
              loading={loading}
              patientFullName={patientFullName}
              countryCode={countryCode}
              listReproved={listReproved}
              isUnableToChoose={isUnableToChoose}
              hasToShowPopOver={hasToShowPopOver}
              setStatus={setStatus}
              countrySettings={countrySettings}
              oldReport={oldReport}
              oldReportCountry={oldReportCountry}
              validatedExams={validatedExams}
              showConfirmReprovingValues={showConfirmReprovingValues}
              setShowConfirmReprovingValues={setShowConfirmReprovingValues}
              generatedPrescriptions={generatedPrescriptions}
              hasDeferredExamPrescription={hasDeferredExamPrescription}
            />
          }
          {currentFormType && (currentFormType !== 'health' || activeConsultationTab === 'health') && (
            <ExamTabs
              from="consultation"
              loading={loading}
              services={serviceDetail}
              countryCode={countryCode}
              isPermittedRole={isPermittedRole}
              serviceParams={serviceParams}
              refreshMedicalConsultationDetails={refreshMedicalConsultationDetails}
              reprovedResultsMap={reprovedResultsMap}
              currentServiceId={medicalConsultationDetails?.service_id}
              countrySettings={countrySettings}
              examListInSidebar={examListInSidebar}
              activeExamTab={activeExamTab}
              setActiveExamTab={setActiveExamTab}
              setExamTabItems={setExamTabItems}
              examContentRef={examContentRef}
            />
          )}
        </Col>
      </Row>
    </Col>
  );
};

export default ConsultationEdit;
